import React, {Fragment, PureComponent} from 'react';
import PropTypes, {shape} from 'prop-types';
import cx from 'classnames';
import {connect} from 'react-redux';

import styles from './RecentlyPlayedButton.pcss';

import Button from '../../../button/Button';
import Message, {getMessage, mapMessageStateToProps} from '../../../message/Message';
import {productButtonStates, serverStatusTypes, themes} from '../../../../constants';
import {formatNumber, getTimestamp} from '../../../../utils/GeneralUtils';

class RecentlyPlayedButton extends PureComponent {
    static propTypes = {
        appName: PropTypes.string.isRequired,
        appTitle: PropTypes.string,
        detailsLink: PropTypes.string.isRequired,
        hasUpdate: PropTypes.bool,
        isProductUnsupported: PropTypes.bool,
        isPromo: PropTypes.bool,
        id: PropTypes.string.isRequired,
        messages: PropTypes.object.isRequired,
        namespace: PropTypes.string.isRequired,
        percent: PropTypes.number,
        productState: PropTypes.string,
        serverStatus: PropTypes.string,
        canPause: PropTypes.bool,
        isPaused: PropTypes.bool,
        isButtonDisabled: PropTypes.bool,
        hasDisabledClass: PropTypes.bool,
        forceUninstall: PropTypes.bool,
        forceProductGet: PropTypes.bool,
        waitTime: shape({
            message: PropTypes.string,
            time: PropTypes.number
        }),
        onLaunchClick: PropTypes.func.isRequired,
        onResumeClick: PropTypes.func.isRequired,
        onRepairClick: PropTypes.func.isRequired,
        onUpdateClick: PropTypes.func.isRequired,
        onInstallClick: PropTypes.func.isRequired,
        onUninstallClick: PropTypes.func.isRequired,
        onInstallingClick: PropTypes.func.isRequired
    };

    handleClicked = callback => () => {
        const {id, namespace, appName} = this.props;

        callback(namespace, id, appName);
    };

    renderLaunchProductButton = () => {
        const {messages, serverStatus, onLaunchClick} = this.props;

        let label = getMessage(messages, 'diesel.common.recently_played.launch');
        let onClick = this.handleClicked(onLaunchClick);

        switch (serverStatus) {
            case serverStatusTypes.OFFLINE:
                label = (
                    <Fragment>
                        <Message code="diesel.common.recently_played.server_offline_multiline1" />
                        <Message
                            className={styles.secondaryText}
                            code="diesel.common.recently_played.server_offline_multiline2"
                        />
                    </Fragment>
                );
                onClick = undefined;
                break;

            case serverStatusTypes.RESTRICTED:
                label = (
                    <Fragment>
                        <Message code="diesel.common.recently_played.launch_restricted_multiline1" />
                        <Message
                            className={styles.secondaryText}
                            code="diesel.common.recently_played.launch_restricted_multiline2"
                        />
                    </Fragment>
                );
                break;

            default:
        }

        return {
            label,
            onClick
        };
    };

    render() {
        const {
            appTitle,
            detailsLink,
            hasUpdate,
            messages,
            percent,
            productState,
            serverStatus,
            waitTime,
            isButtonDisabled,
            hasDisabledClass,
            canPause,
            isPaused,
            isProductUnsupported,
            isPromo,
            forceUninstall,
            forceProductGet,
            onResumeClick,
            onRepairClick,
            onUpdateClick,
            onInstallClick,
            onUninstallClick,
            onInstallingClick
        } = this.props;

        let buttonLabel;
        let buttonTo;
        let buttonOnClick = null;

        switch (productState) {
            case productButtonStates.LAUNCH: {
                const {label, onClick} = this.renderLaunchProductButton();
                buttonLabel = label;
                buttonOnClick = onClick;
                break;
            }
            case productButtonStates.RESUME: {
                buttonLabel = getMessage(messages, 'diesel.common.recently_played.resume');
                buttonOnClick = this.handleClicked(onResumeClick);
                break;
            }
            case productButtonStates.REPAIR: {
                buttonLabel = getMessage(messages, 'diesel.common.recently_played.repair');
                buttonOnClick = this.handleClicked(onRepairClick);
                break;
            }
            case productButtonStates.INSTALL: {
                buttonLabel = getMessage(messages, 'diesel.common.recently_played.install');
                buttonOnClick = this.handleClicked(onInstallClick);
                break;
            }
            case productButtonStates.INSTALLING: {
                const updateCode = hasUpdate ? 'updating' : 'installing';
                buttonLabel = (
                    <Fragment>
                        <Message
                            code={`diesel.common.recently_played.${
                                isPaused ? 'paused' : updateCode
                            }`}
                        />
                        <Message
                            className={styles.secondaryText}
                            code="diesel.common.recently_played.percent_left"
                            args={[formatNumber(Math.round(percent), 0)]}
                        />
                    </Fragment>
                );
                buttonOnClick = isPaused
                    ? this.handleClicked(onResumeClick)
                    : this.handleClicked(onInstallingClick);

                if (!canPause) {
                    buttonOnClick = null;
                }
                break;
            }
            case productButtonStates.UPDATE: {
                buttonLabel = getMessage(messages, 'diesel.common.recently_played.update');
                buttonOnClick = this.handleClicked(onUpdateClick);
                break;
            }
            case productButtonStates.QUEUED: {
                buttonLabel = getMessage(messages, 'diesel.common.recently_played.queued');
                break;
            }
            case productButtonStates.BANNED: {
                buttonLabel = getMessage(messages, 'diesel.common.recently_played.banned');
                break;
            }
            case productButtonStates.RUNNING: {
                buttonLabel = getMessage(messages, 'diesel.common.recently_played.running', [
                    appTitle
                ]);
                break;
            }
            case productButtonStates.WAITING: {
                buttonLabel = (
                    <Fragment>
                        <Message code="diesel.common.recently_played.waiting" />
                        {waitTime && waitTime.time > 0 && (
                            <Message
                                className={styles.secondaryText}
                                code={`diesel.common.recently_played.waiting_${waitTime.message}`}
                                args={[getTimestamp(waitTime.time)]}
                            />
                        )}
                    </Fragment>
                );
                break;
            }
            case productButtonStates.GET: {
                buttonLabel = null; // handled below
                break;
            }
            default:
                buttonLabel = getMessage(messages, 'diesel.common.recently_played.loading');
        }

        if (isProductUnsupported) {
            buttonLabel = getMessage(messages, 'diesel.common.recently_played.unsupported');
        }

        if (isPromo || forceProductGet) {
            buttonLabel = getMessage(messages, 'diesel.common.recently_played.view');
            buttonTo = detailsLink;
        }

        if (forceUninstall) {
            buttonLabel = getMessage(messages, 'diesel.common.recently_played.uninstall');
            buttonOnClick = this.handleClicked(onUninstallClick);
            buttonTo = undefined;
        }

        const isOpaque =
            productState === productButtonStates.WAITING ||
            serverStatus === serverStatusTypes.OFFLINE;

        const buttonClassName = cx(styles.button, {
            [styles.opaque]: isOpaque
        });

        return (
            buttonLabel && (
                <Button
                    className={buttonClassName}
                    theme={hasDisabledClass ? themes.BLACK : themes.DARK}
                    hasMinWidth={false}
                    disabled={hasDisabledClass || isButtonDisabled}
                    onClick={buttonOnClick}
                    to={buttonTo}>
                    {buttonLabel}
                </Button>
            )
        );
    }
}

export default connect(mapMessageStateToProps)(RecentlyPlayedButton);
